import type { ReactNode } from 'react';
import type { DataConfidence, ExecutiveInsight, ExecutiveSummary } from '@ace-tracker/shared';
import { EXECUTIVE_METRIC_INFO } from '../../metricInfo';
import { TOOL_COLORS } from '../../toolColors';
import { Button } from '../Button';
import { InfoTooltip } from '../InfoTooltip';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { ExecutiveTrendChart } from './ExecutiveTrendChart';
import { buildKpis, formatGeneratedAt, formatPercent, formatToolMoney, NO_COMPARISON_LABEL, pointsChange, spendExclusionNote, TOOL_NAMES, toolsInUse, type Delta, type KpiView } from './format';

const KPI_INFO: Record<KpiView['key'], string> = {
  active: EXECUTIVE_METRIC_INFO.activeDevelopers,
  adoption: EXECUTIVE_METRIC_INFO.adoptionRate,
  habitual: EXECUTIVE_METRIC_INFO.habitualUsers,
  hours: EXECUTIVE_METRIC_INFO.activeHours,
  spend: EXECUTIVE_METRIC_INFO.indicativeSpend,
};

const DEPTH_COLORS = ['bg-depth-1', 'bg-depth-2', 'bg-depth-3'];

/**
 * The Executive view: five headline numbers, four panels, and auto-written
 * insights, sized to fill one screen (see AppShell `fitViewport`) and
 * mirrored one-to-one by the downloaded slide (exportSlide.ts).
 */
export function ExecutiveDashboardBody({
  summary,
  confidence,
  onOpenSettings,
  onOpenHealth,
}: {
  summary: ExecutiveSummary;
  /** How far the figures can be trusted; omitted while data health is loading or unavailable. */
  confidence?: DataConfidence;
  onOpenSettings: () => void;
  onOpenHealth: () => void;
}) {
  const kpis = buildKpis(summary);

  return (
    <div
      // Panel rows share the spare height but never shrink below their content
      // (min-content), so a short screen can't push one row's panels over the
      // next. The grid grows the page instead, and fills it exactly when it fits.
      className={`grid gap-4 fit:flex-1 ${
        confidence
          ? 'fit:grid-rows-[auto_auto_minmax(min-content,1.15fr)_minmax(min-content,1fr)_auto]'
          : 'fit:grid-rows-[auto_minmax(min-content,1.15fr)_minmax(min-content,1fr)_auto]'
      }`}
    >
      {confidence && <ConfidenceIndicator confidence={confidence} onOpenHealth={onOpenHealth} />}
      <section aria-label="Headline metrics" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {kpis.map((kpi) => (
          <KpiTile key={kpi.key} kpi={kpi} info={KPI_INFO[kpi.key]} noComparisonReason={summary.comparison.available ? undefined : (summary.comparison.reason ?? NO_COMPARISON_LABEL)}>
            {kpi.key === 'adoption' && summary.adoption.current !== null && (
              <TargetBar value={summary.adoption.current} target={summary.adoption.targetPercent} />
            )}
          </KpiTile>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)]">
        <Panel title="Active developers, rolling 28 days" hint="Weekly · last 12 weeks" info={EXECUTIVE_METRIC_INFO.trend} className="h-80 fit:h-auto fit:min-h-[260px]">
          <ExecutiveTrendChart points={summary.trend} target={summary.targetDevelopers} />
        </Panel>
        <Panel title="Tool footprint" hint="This period" info={EXECUTIVE_METRIC_INFO.toolFootprint}>
          <ToolFootprint summary={summary} />
        </Panel>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Panel title="Adoption by team" hint={`Marker = ${summary.adoption.targetPercent}% target`} info={EXECUTIVE_METRIC_INFO.teamAdoption} className="min-h-[240px] fit:min-h-[200px]">
          <TeamAdoption summary={summary} onOpenSettings={onOpenSettings} />
        </Panel>
        <Panel title="Depth of use" hint="Active days per developer" info={EXECUTIVE_METRIC_INFO.depthOfUse} className="min-h-[240px] fit:min-h-[200px]">
          <DepthOfUse summary={summary} />
        </Panel>
        <Panel title="What leadership should know" info={EXECUTIVE_METRIC_INFO.insights} className="min-h-[240px] md:col-span-2 lg:col-span-1 fit:min-h-[200px]">
          <Insights insights={summary.insights} />
        </Panel>
      </section>

      <footer className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-text-faint">
        <span>Developers counted once across tools</span>
        <span>Active = made at least one AI request that day</span>
        <span title={EXECUTIVE_METRIC_INFO.indicativeSpend}>
          Spend is indicative token pricing ({summary.spend.source}), not an invoice
          {spendExclusionNote(summary) && `; ${spendExclusionNote(summary)}`}
        </span>
        <span>Small teams are grouped as "Other"</span>
        <span>Generated {formatGeneratedAt(summary.generatedAt)}</span>
      </footer>
    </div>
  );
}

function Panel({
  title,
  hint,
  info,
  className = '',
  children,
}: {
  title: string;
  hint?: string;
  info?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <article className={`flex min-w-0 flex-col rounded-card border border-border bg-surface p-4 shadow-sm ${className}`}>
      <header className="mb-3 flex items-baseline gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
          {title}
          {info && <InfoTooltip text={info} />}
        </h2>
        {hint && <span className="ml-auto text-right text-[11px] text-text-faint">{hint}</span>}
      </header>
      <div className="min-h-0 flex-1">{children}</div>
    </article>
  );
}

function KpiTile({
  kpi,
  info,
  noComparisonReason,
  children,
}: {
  kpi: KpiView;
  info: string;
  /** Set when the previous period is not fully retained: shown in place of the change. */
  noComparisonReason?: string;
  children?: ReactNode;
}) {
  return (
    <div className="relative flex min-w-0 flex-col rounded-card border border-border bg-surface px-4 py-3 shadow-sm">
      <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card bg-primary-blue" />
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-text-muted">
        {kpi.label}
        <InfoTooltip text={info} />
      </p>
      <p className="mt-1 text-[30px] font-bold leading-tight tabular-nums text-text-default">{kpi.value}</p>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted">
        {kpi.delta && <DeltaChip delta={kpi.delta} />}
        {noComparisonReason && (
          <span title={noComparisonReason} className="rounded border border-line-soft bg-surface-alt px-1.5 py-0.5 font-medium text-text-muted">
            n/a<span className="sr-only"> — previous period incomplete</span>
          </span>
        )}
        <span className="tabular-nums">{kpi.context}</span>
      </p>
      {children}
    </div>
  );
}

const DELTA_TONES: Record<Delta['tone'], string> = {
  good: 'bg-ok-bg text-ok-ink',
  bad: 'bg-crit-bg text-crit-ink',
  neutral: 'border border-line-soft bg-surface-alt text-text-default',
};
const DELTA_ICONS: Record<Delta['direction'], string> = { up: 'fa-caret-up', down: 'fa-caret-down', flat: 'fa-minus' };
const DELTA_WORDS: Record<Delta['direction'], string> = { up: 'Up', down: 'Down', flat: 'No change,' };

/** Change vs the previous period. Direction is carried by icon and words as well as color. */
function DeltaChip({ delta }: { delta: Delta }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-bold tabular-nums ${DELTA_TONES[delta.tone]}`}>
      <i aria-hidden="true" className={`fa-solid ${DELTA_ICONS[delta.direction]}`} />
      <span className="sr-only">{DELTA_WORDS[delta.direction]}</span>
      {delta.direction !== 'flat' && delta.text}
      <span className="sr-only"> vs previous period</span>
    </span>
  );
}

function TargetBar({ value, target }: { value: number; target: number }) {
  return (
    <div
      role="img"
      aria-label={`${Math.round(value)}% adopted against a ${target}% target`}
      className="relative mt-2 h-1.5 rounded bg-line-soft"
    >
      <span className="absolute inset-y-0 left-0 rounded bg-primary-blue" style={{ width: `${Math.min(100, value)}%` }} />
      <span className="absolute -inset-y-1 w-0.5 bg-text-default" style={{ left: `${target}%` }} />
    </div>
  );
}

function ToolFootprint({ summary }: { summary: ExecutiveSummary }) {
  const tools = toolsInUse(summary);
  const maxDevelopers = Math.max(1, ...tools.map((t) => t.activeDevelopers));
  const { currency } = summary.spend;
  return (
    <div className="flex h-full flex-col">
      <table className="w-full text-left text-sm tabular-nums">
        <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
          <tr>
            <th scope="col" className="pb-2 font-medium">Tool</th>
            <th scope="col" className="pb-2 text-right font-medium">Developers</th>
            <th scope="col" className="pb-2 text-right font-medium">Spend</th>
            <th scope="col" className="pb-2 text-right font-medium">Per dev</th>
          </tr>
        </thead>
        <tbody>
          {tools.map((t) => (
            <tr key={t.tool} className="border-b border-line-soft last:border-0">
              <td className="py-2 pr-2">
                <span className="flex items-center gap-2 whitespace-nowrap font-medium text-text-default">
                  <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: TOOL_COLORS[t.tool] }} />
                  {TOOL_NAMES[t.tool]}
                </span>
              </td>
              <td className="py-2 pl-2 text-right">
                <b className="text-text-default">{t.activeDevelopers}</b>
                <span
                  aria-hidden="true"
                  className="ml-auto mt-1 block h-1 rounded"
                  style={{ width: `${Math.round((t.activeDevelopers / maxDevelopers) * 100)}%`, backgroundColor: TOOL_COLORS[t.tool] }}
                />
              </td>
              <td className="py-2 pl-2 text-right text-text-default" title={t.spend === null ? EXECUTIVE_METRIC_INFO.spendNotIncluded : undefined}>
                {formatToolMoney(t.spend, currency)}
                {t.spend === null && <span className="sr-only">Not included</span>}
              </td>
              <td className="py-2 pl-2 text-right text-text-muted">{formatToolMoney(t.spendPerDeveloper, currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-auto pt-3 text-[11px] leading-snug text-text-faint">
        <b className="font-medium text-text-default">{summary.multiToolDevelopers} developers</b> use more than one tool, so tool counts can
        add up to more than {summary.activeDevelopers.current}. Spend is indicative token pricing
        {spendExclusionNote(summary) && <>; {spendExclusionNote(summary)} (—)</>}.
      </p>
    </div>
  );
}

function TeamAdoption({ summary, onOpenSettings }: { summary: ExecutiveSummary; onOpenSettings: () => void }) {
  const target = summary.adoption.targetPercent;
  if (summary.teams.length === 0) {
    return (
      <div className="flex h-full flex-col items-start justify-center gap-2 text-sm text-text-muted">
        <p>No teams to show yet. Assign developers to teams to see adoption by team.</p>
        <Button variant="text" className="!min-h-[44px] text-sm" onClick={onOpenSettings}>
          Open Settings → Developers
          <i aria-hidden="true" className="fa-solid fa-arrow-right text-xs" />
        </Button>
      </div>
    );
  }
  return (
    // contain:size: a long team list scrolls inside its panel instead of growing the row.
    <ul className="flex h-full flex-col justify-between gap-1.5 overflow-y-auto fit:[contain:size]">
      {summary.teams.map((t) => {
        const below = t.adoptionPercent < target;
        return (
          <li key={t.team} className="grid grid-cols-[minmax(84px,38%)_minmax(0,1fr)_3.25rem] items-center gap-3 text-xs">
            <span className="truncate text-text-default" title={`${t.team}: ${t.active} of ${t.members} active`}>
              {t.team}
              <span className="ml-1 text-text-faint">{t.members}</span>
            </span>
            <span className="relative h-2.5 rounded bg-line-soft">
              <span
                className="absolute inset-y-0 left-0 rounded bg-primary-blue"
                style={{
                  width: `${Math.min(100, t.adoptionPercent)}%`,
                  // Below target is shown by pattern as well as the marker, never color alone.
                  backgroundImage: below ? 'repeating-linear-gradient(135deg, transparent 0 3px, var(--surface) 3px 5px)' : undefined,
                }}
              />
              <span aria-hidden="true" className="absolute -inset-y-1 w-0.5 bg-text-default" style={{ left: `${target}%` }} />
            </span>
            <span className="text-right font-bold tabular-nums text-text-default">
              {formatPercent(t.adoptionPercent)}
              {below && <span className="sr-only"> (below target)</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function DepthOfUse({ summary }: { summary: ExecutiveSummary }) {
  const current = summary.depth.reduce((sum, d) => sum + d.current, 0);
  const previous = summary.depth.reduce((sum, d) => sum + d.previous, 0);
  const agentic = pointsChange(summary.agenticShare.current, summary.agenticShare.previous);
  const describe = (field: 'current' | 'previous') => summary.depth.map((d) => `${d.label} ${d[field]}`).join(', ');

  return (
    <div className="flex h-full flex-col">
      <DepthBar label="This period" total={current} values={summary.depth.map((d) => d.current)} description={describe('current')} />
      {summary.comparison.available ? (
        <DepthBar label="Previous" total={previous} values={summary.depth.map((d) => d.previous)} description={describe('previous')} muted />
      ) : (
        <p className="mb-2 text-[11px] text-text-faint" title={summary.comparison.reason ?? undefined}>
          Previous: {NO_COMPARISON_LABEL}
        </p>
      )}
      <ul className="mt-1 grid gap-1 text-[11px] text-text-muted">
        {summary.depth.map((d, i) => (
          <li key={d.key} className="flex items-center gap-2">
            <span aria-hidden="true" className={`h-2.5 w-2.5 shrink-0 rounded-sm ${DEPTH_COLORS[i]}`} />
            {d.label}
            <span className="text-text-faint">{d.description}</span>
            <b className="ml-auto font-medium tabular-nums text-text-default">{d.current}</b>
          </li>
        ))}
      </ul>
      <div className="mt-auto flex items-center gap-3 border-t border-line-soft pt-3 text-xs text-text-muted">
        <strong className="text-lg font-bold tabular-nums text-text-default">{formatPercent(summary.agenticShare.current)}</strong>
        <span className="flex items-center gap-1">
          of requests are agentic
          <InfoTooltip text={EXECUTIVE_METRIC_INFO.agenticShare} />
        </span>
        {agentic && (
          <span className="ml-auto">
            <DeltaChip delta={agentic} />
          </span>
        )}
      </div>
    </div>
  );
}

function DepthBar({
  label,
  total,
  values,
  description,
  muted,
}: {
  label: string;
  total: number;
  values: number[];
  description: string;
  muted?: boolean;
}) {
  return (
    <div className="mb-2 grid grid-cols-[4.75rem_minmax(0,1fr)_2.5rem] items-center gap-3 text-xs">
      <span className={muted ? 'text-text-muted' : 'text-text-default'}>{label}</span>
      <div role="img" aria-label={`${label}: ${description}`} className="flex h-[18px] gap-0.5 overflow-hidden rounded bg-line-soft">
        {total > 0 &&
          values.map((v, i) => (
            <span key={i} className={`h-full ${DEPTH_COLORS[i]}`} style={{ width: `${(v / total) * 100}%` }} />
          ))}
      </div>
      <span className={`text-right tabular-nums ${muted ? 'text-text-muted' : 'font-bold text-text-default'}`}>{total}</span>
    </div>
  );
}

const INSIGHT_STYLES: Record<ExecutiveInsight['kind'], { icon: string; className: string; label: string }> = {
  positive: { icon: 'fa-arrow-trend-up', className: 'bg-ok-bg text-ok-ink', label: 'Positive' },
  attention: { icon: 'fa-triangle-exclamation', className: 'bg-warn-bg text-warn-ink', label: 'Needs attention' },
  note: { icon: 'fa-circle-info', className: 'border border-line-soft bg-surface-alt text-primary-blue', label: 'Note' },
};

function Insights({ insights }: { insights: ExecutiveInsight[] }) {
  if (insights.length === 0) return <p className="text-sm text-text-muted">Nothing notable in this period.</p>;
  return (
    // contain:size: long insights scroll inside the panel instead of growing the row.
    <ul className="flex h-full flex-col gap-3 overflow-y-auto fit:[contain:size]">
      {insights.map((insight, i) => {
        const style = INSIGHT_STYLES[insight.kind];
        return (
          <li key={i} className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2.5 text-[13px] leading-snug text-text-default">
            <span className={`flex h-6 w-6 items-center justify-center rounded-md text-[11px] ${style.className}`}>
              <i aria-hidden="true" className={`fa-solid ${style.icon}`} />
              <span className="sr-only">{style.label}:</span>
            </span>
            <span>{insight.text}</span>
          </li>
        );
      })}
    </ul>
  );
}
